import { darken, getContrastRatio } from '@mui/material/styles';
import { SocialPlatform } from '../types';

/**
 * Single source of truth for which platforms exist, whether they can actually
 * publish, and how they are labelled and coloured.
 *
 * Set `status` to control visibility across the whole app:
 *   'available'   - fully working; offered for connecting and posting
 *   'coming-soon' - shown but disabled, so the UI does not promise what it cannot do
 *   'hidden'      - not shown anywhere
 *
 * Keep this in step with the server: a platform is only 'available' if
 * `POST /api/posts/:id/publish` has a real integration for it, otherwise the
 * publish attempt will fail with "not implemented yet".
 */
export type PlatformStatus = 'available' | 'coming-soon' | 'hidden';

export interface PlatformConfig {
  id: SocialPlatform;
  name: string;
  color: string;
  status: PlatformStatus;
  /** Maximum characters the platform accepts in a post body. */
  charLimit: number;
  /** Media is mandatory, not optional (Instagram). */
  requiresMedia?: boolean;
  /** Platform will reject media entirely (LinkedIn, via this app's text-only path). */
  supportsMedia?: boolean;
  /**
   * A published post can be removed from the platform through its API, so deleting here
   * can take the live copy down too. Mirrors REMOTE_DELETE_PLATFORMS in server/api.js —
   * the server performs the delete, this drives what the confirmation dialog promises.
   * A platform without it is deleted from ShoutVector only, and the dialog says so
   * rather than implying the live post has gone.
   */
  supportsRemoteDelete?: boolean;
  /** Why a remote delete may not work yet — shown in the confirmation dialog. */
  remoteDeleteNote?: string;
  /**
   * Some platforms have no OAuth flow of their own — Instagram is reached through
   * the Facebook connection. Set this to the platform that owns the connection so
   * the UI can point at it instead of offering a Connect button that cannot work.
   */
  connectsVia?: SocialPlatform;
  /** Constraint or explanation worth showing on the platform's card. */
  note?: string;
}

export const PLATFORMS: PlatformConfig[] = [
  {
    id: 'facebook',
    name: 'Facebook',
    color: '#4267B2',
    status: 'available',
    charLimit: 63206,
    supportsMedia: true,
    supportsRemoteDelete: true,
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    color: '#0077B5',
    status: 'available',
    charLimit: 3000,
    // Images (up to 9) or one video, uploaded via the assets API — not both in one post.
    // Video since 2026-10-17; the server side is server/video-upload.js.
    supportsMedia: true,
    supportsRemoteDelete: true,
  },
  {
    id: 'twitter',
    name: 'X (Twitter)',
    color: '#1DA1F2',
    status: 'available',
    charLimit: 280,
    // Up to 4 images via /2/media/upload, or one video via the chunked upload (since
    // 2026-10-17, server/video-upload.js) — X does not mix them in one post.
    supportsMedia: true,
    supportsRemoteDelete: true,
  },
  {
    id: 'instagram',
    name: 'Instagram',
    color: '#E4405F',
    status: 'available',
    charLimit: 2200,
    requiresMedia: true,
    supportsMedia: true,
    // Instagram has no OAuth of its own here: accounts are discovered through the
    // Facebook connection, so its card is managed under Facebook.
    connectsVia: 'facebook',
    note: 'Every Instagram post must include an image or video — text-only posts are rejected.',
    supportsRemoteDelete: true,
    // instagram_manage_contents was added to the Facebook scope after the current token was
    // issued, and a scope only arrives on a reconnect — the same pattern as media.write on X
    // and read_insights on Facebook. Until then Instagram deletes fail with a permission
    // error, which the dialog warns about rather than letting it surprise anyone.
    remoteDeleteNote:
      'Removing an Instagram post needs a Facebook reconnect first — the permission to delete ' +
      'was added after this connection was authorised.',
  },
  {
    id: 'threads',
    name: 'Threads',
    color: '#000000',
    status: 'coming-soon',
    charLimit: 500,
    note: 'No integration yet.',
  },
  {
    id: 'bluesky',
    name: 'Bluesky',
    color: '#1185FE',
    status: 'coming-soon',
    charLimit: 300,
    note: 'No integration yet.',
  },
];

const BY_ID = new Map(PLATFORMS.map(p => [p.id, p]));

/** Platforms offered for connecting and posting. */
export const availablePlatforms = () => PLATFORMS.filter(p => p.status === 'available');

/** Everything worth rendering — available plus disabled "coming soon" entries. */
export const visiblePlatforms = () => PLATFORMS.filter(p => p.status !== 'hidden');

export const getPlatform = (id: string): PlatformConfig | undefined => BY_ID.get(id as SocialPlatform);

export const getPlatformName = (id: string): string => BY_ID.get(id as SocialPlatform)?.name ?? id;

export const getPlatformColor = (id: string): string => BY_ID.get(id as SocialPlatform)?.color ?? '#666';

export const isPlatformAvailable = (id: string): boolean =>
  BY_ID.get(id as SocialPlatform)?.status === 'available';

/** Characters allowed by the strictest selected platform, or null if none selected. */
export const strictestCharLimit = (ids: string[]): number | null => {
  const limits = ids.map(id => BY_ID.get(id as SocialPlatform)?.charLimit).filter((n): n is number => typeof n === 'number');
  return limits.length ? Math.min(...limits) : null;
};

export const getCharLimit = (id: string): number | null =>
  BY_ID.get(id as SocialPlatform)?.charLimit ?? null;

/**
 * Public URL for a published post.
 *
 * A permalink the platform itself reported always wins. Everything else is a URL *derived*
 * from the id, which only works where the id appears in the URL — and those patterns are
 * assumptions about someone else's routing, so if a network changes its scheme every derived
 * link in the app breaks at once. A stored permalink cannot rot that way.
 *
 * Instagram is why this exists: `media_publish` returns a numeric media id, and Instagram's
 * public URLs use a short code (/p/DdmwlZ1E5MS/) that cannot be computed from it. Instagram
 * posts therefore had no link at all until the permalink was fetched and stored at publish
 * time. Posts published before that have no stored permalink and still fall through to `null`
 * unless they were backfilled.
 *
 * Returns null when there is neither a stored permalink nor a derivable one — the callers all
 * render no link rather than a broken one.
 */
/**
 * A platform's brand colour, darkened just enough to be readable as text on a light background.
 *
 * The raw brand colours are made for fills, not type. X's #1DA1F2 is 2.83:1 against white and
 * Bluesky's #1185FE is 3.61:1 — both below the 4.5:1 needed for normal text, and the chips
 * that use this are 0.7rem in places. Facebook and LinkedIn already pass and are returned
 * untouched, so nothing is dulled that did not need to be.
 *
 * Darkening rather than substituting keeps the colour recognisably the platform's: a darker
 * X blue still reads as X, where a generic grey or a theme colour would not.
 */
export const getPlatformTextColor = (id: string): string => {
  const base = getPlatformColor(id);
  let colour = base;
  // Step rather than jump, so a colour that already passes is left alone and one that does not
  // is changed as little as possible.
  for (let step = 0; step < 12 && getContrastRatio(colour, '#fff') < 4.5; step++) {
    colour = darken(base, 0.08 * (step + 1));
  }
  return colour;
};

export const platformPostUrl = (
  platform: string,
  platformPostId?: string | null,
  permalink?: string | null
): string | null => {
  if (permalink) return permalink;
  if (!platformPostId) return null;
  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/${platformPostId}`;
    case 'twitter':
      return `https://twitter.com/i/web/status/${platformPostId}`;
    case 'linkedin':
      return `https://www.linkedin.com/feed/update/${platformPostId}`;
    // Instagram has no derivable URL — it depends entirely on the stored permalink above
    default:
      return null;
  }
};

/**
 * Can a published post be taken down from this platform through its API?
 *
 * Drives what the delete confirmation promises. Saying "this will also remove it from
 * Facebook" when it would not is the kind of false success this codebase has been bitten
 * by before, so the dialog splits the targeted platforms by this flag and names both sets.
 */
export const supportsRemoteDelete = (id: string): boolean =>
  Boolean(BY_ID.get(id as SocialPlatform)?.supportsRemoteDelete);

export const remoteDeleteNote = (id: string): string | null =>
  BY_ID.get(id as SocialPlatform)?.remoteDeleteNote ?? null;
